import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { ApiError, clearAuthCircuit, getMe, loginUser, logoutUser, registerUser, type User, type UserRole } from "@/lib/api";
import { db } from "@/lib/db";
import { pruneSynced, pruneSyncedFieldNotes, resumeAfterLogin, resumeFieldNotesAfterLogin } from "@/lib/outbox";
import { requestSync, startSyncScheduler } from "@/lib/syncScheduler";
import { initializeTelemetry, setUserRole } from "@/lib/telemetry";

const LAST_USER_KEY = "lastUser";

type AuthContextValue = {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string, turnstileToken: string, deviceFingerprint?: string, clientMeta?: Record<string, unknown>) => Promise<void>;
  register: (payload: { email: string; password: string; full_name: string; company_name: string; role: UserRole; turnstile_token: string; device_fingerprint?: string; client_metadata?: Record<string, unknown> }) => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = React.createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const {
    data,
    isError,
    error,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      try {
        const user = await getMe();
        clearAuthCircuit();
        try {
          await db.kv.put({ key: LAST_USER_KEY, value: user });
          // Ensure Dexie/IDB connection is healthy early (helps avoid "connection lost" on first enqueue).
          try { await db.open(); } catch {}
          // Best-effort: request persistent storage so browser is less likely to evict our outbox.
          void (async () => {
            try {
              if (navigator.storage?.persist) await navigator.storage.persist();
            } catch {}
          })();
        } catch (storageErr) {
          // Private browsing / broken IDB must never block login.
          const { markStorageDegraded } = await import("@/lib/storageCapability");
          markStorageDegraded(
            `auth.kv_put: ${storageErr instanceof Error ? storageErr.message : String(storageErr)}`,
          );
        }
        return user;
      } catch (error) {
        if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
          // The session is genuinely gone — drop the cached identity.
          await db.kv.delete(LAST_USER_KEY).catch(() => undefined);
          throw error;
        }
        // Network failure (e.g. offline cold start of the PWA): fall back to
        // the last known session so the user can still punch. The sync engine
        // re-verifies the session against the server before uploading.
        const cached = await db.kv.get(LAST_USER_KEY).catch(() => undefined);
        if (cached?.value) return cached.value as User;
        throw error;
      }
    },
    retry: false,
    staleTime: 5 * 60 * 1000, // roles/company rarely change; rely on explicit invalidates + online
    // Logged-out / expired sessions must not re-poll /auth/me on every reconnect
    // (field devices leave the PWA open for hours on flaky radio).
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  });

  // Whenever an authenticated session exists (fresh login, MFA completion, or
  // page load with a valid cookie), release any punches that were parked
  // waiting for sign-in and run the background sync scheduler.
  const userId = data?.id;
  React.useEffect(() => {
    if (userId === undefined) return;
    void resumeAfterLogin(userId).then(() => requestSync());
    void resumeFieldNotesAfterLogin(userId);
    void pruneSynced();
    void pruneSyncedFieldNotes();
    return startSyncScheduler();
  }, [userId]);

  // Treat real auth failures (401/403) as "not logged in". This makes the
  // guard and the login page's auto-redirect logic reliable even when the
  // query ends up in an error state instead of having data.
  const isAuthError =
    isError &&
    error instanceof ApiError &&
    (error.status === 401 || error.status === 403);

  // On coming back online, re-verify session only when we still believe we are
  // (or might be) logged in — not when we already know credentials are dead.
  React.useEffect(() => {
    const onOnline = () => {
      const state = queryClient.getQueryState(["me"]);
      const err = state?.error;
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        return;
      }
      void queryClient.invalidateQueries({ queryKey: ["me"] });
    };
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, [queryClient]);

  // When the session dies, cancel sibling dashboard queries so we don't fan out
  // 6–7 parallel 401s on cellular.
  React.useEffect(() => {
    if (!isAuthError) return;
    void queryClient.cancelQueries({
      predicate: (q) => {
        const key = q.queryKey[0];
        return key !== "me";
      },
    });
  }, [isAuthError, queryClient]);

  // Feed authenticated user role into telemetry for user properties + decision engine.
  // Safe and idempotent; initializeTelemetry is already kicked in main.
  React.useEffect(() => {
    if (data?.role) {
      void initializeTelemetry(data);
      setUserRole(data.role);
    }
  }, [data?.role, data?.id]);

  const user = isAuthError ? null : (data ?? null);
  const loading = !isAuthError && (isLoading || (!data && isFetching));

  const value = React.useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      login: async (email, password, turnstileToken, deviceFingerprint, clientMeta) => {
        clearAuthCircuit();
        await loginUser({ email, password, turnstile_token: turnstileToken, device_fingerprint: deviceFingerprint, client_metadata: clientMeta });
        // Force a fresh /me (cookie now set) so ProtectedRoute + effects see the user.
        await queryClient.refetchQueries({ queryKey: ["me"], type: "active" });
      },
      register: async (payload) => {
        clearAuthCircuit();
        await registerUser(payload);
        // fire and forget the follow ups to avoid sequential await flag (login must logically follow)
        loginUser({ email: payload.email, password: payload.password, turnstile_token: payload.turnstile_token, device_fingerprint: payload.device_fingerprint, client_metadata: payload.client_metadata }).catch(() => {});
        queryClient.refetchQueries({ queryKey: ["me"], type: "active" }).catch(() => {});
      },
      logout: async () => {
        try {
          await logoutUser();
        } catch {
          // ignore errors during logout
        }
        await db.kv.delete(LAST_USER_KEY).catch(() => undefined);
        clearAuthCircuit();

        queryClient.clear();
        queryClient.setQueryData(["me"], null);
        queryClient.cancelQueries({ queryKey: ["me"] });

        navigate("/login", { replace: true, state: { fromLogout: true } });
      },
    }),
    [user, loading, queryClient, navigate]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = React.useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider.");
  return context;
}
